/**
 * Panel/Navigation state management for Creator Studio
 *
 * Controls sidebar state, active section, and expanded sections.
 *
 * OTTO Platform Governance: sidebar-reference v1
 */

import { create } from 'zustand';

export type SectionId = 'studio' | 'projects' | 'templates' | 'assets' | 'exports' | 'brands';

interface PanelState {
  sidebarExpanded: boolean;
  setSidebarExpanded: (expanded: boolean) => void;
  toggleSidebar: () => void;

  activeSection: SectionId;
  setActiveSection: (section: SectionId) => void;

  expandedSections: Set<SectionId>;
  toggleSection: (section: SectionId) => void;

  collapseSidebar: () => void;
  expandSidebar: (section?: SectionId) => void;
}

export const usePanelStore = create<PanelState>((set, get) => ({
  sidebarExpanded: false,
  setSidebarExpanded: (expanded) => set({ sidebarExpanded: expanded }),
  toggleSidebar: () => set((state) => ({ sidebarExpanded: !state.sidebarExpanded })),

  activeSection: 'studio',
  setActiveSection: (section) => set({ activeSection: section }),

  expandedSections: new Set<SectionId>(),
  toggleSection: (section) => set((state) => {
    const next = new Set(state.expandedSections);
    if (next.has(section)) {
      next.delete(section);
    } else {
      next.add(section);
    }
    return { expandedSections: next };
  }),

  collapseSidebar: () => set({
    sidebarExpanded: false,
    expandedSections: new Set<SectionId>(),
  }),

  expandSidebar: (section) => set((state) => ({
    sidebarExpanded: true,
    activeSection: section || state.activeSection,
  })),
}));
